import { useCallback } from "react";
import { Checkbox, Flex, Text } from "@itwin/itwinui-react";
import { IEventDefinition } from "../../interfaces/IEventDefinition";

export interface EventSelectorProps {
  /** Available event definitions from the producer */
  eventDefinitions: IEventDefinition[];
  /** Currently selected event types */
  selectedEvents: string[];
  /** Whether "All events" mode is enabled (includes future events) */
  useAllEvents: boolean;
  /** Called when event selection changes */
  onEventsChange: (events: string[], useAllEvents: boolean) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export const EventSelector = (props: EventSelectorProps) => {
  const {
    eventDefinitions,
    selectedEvents,
    useAllEvents,
    onEventsChange,
    disabled = false,
  } = props;

  const handleAllEventsToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        // When enabling "All events", clear the explicit selection
        onEventsChange([], true);
      } else {
        // When disabling "All events", select all current events explicitly
        const allEventTypes = eventDefinitions.map((def) => def.eventType);
        onEventsChange(allEventTypes, false);
      }
    },
    [eventDefinitions, onEventsChange]
  );

  const handleEventToggle = useCallback(
    (eventType: string, checked: boolean) => {
      let newEvents: string[];
      if (checked && !selectedEvents.includes(eventType)) {
        newEvents = [...selectedEvents, eventType];
      } else if (!checked && selectedEvents.includes(eventType)) {
        newEvents = selectedEvents.filter((e) => e !== eventType);
      } else {
        return; // No change needed
      }
      onEventsChange(newEvents, false);
    },
    [selectedEvents, onEventsChange]
  );

  return (
    <div>
      <Flex flexDirection="column" gap="xs">
        <Checkbox
          label={
            <span>
              All events{" "}
              <Text
                as="span"
                isMuted
                style={{ fontSize: "0.85em" }}
                title="Includes all current and future events from this producer"
              >
                *
              </Text>
            </span>
          }
          checked={useAllEvents}
          onChange={(e) => handleAllEventsToggle(e.target.checked)}
          disabled={disabled}
        />
        {useAllEvents && (
          <Text
            isMuted
            style={{ fontSize: "0.85em", marginLeft: "24px", marginTop: "-4px" }}
          >
            * Includes all future events added by this producer
          </Text>
        )}
      </Flex>

      {/* Always show individual events */}
      <Flex
        flexDirection="row"
        flexWrap="wrap"
        style={{
          marginTop: "12px",
          marginLeft: "8px",
          padding: "8px",
          backgroundColor: useAllEvents ? "var(--iui-color-background-disabled)" : undefined,
          borderRadius: "4px",
          opacity: useAllEvents ? 0.7 : 1,
        }}
        gap="xs"
      >
        {eventDefinitions.map((eventDef) => (
          <Flex.Item flex="1 1 45%" key={eventDef.eventType}>
            <Checkbox
              label={eventDef.eventType}
              checked={useAllEvents || selectedEvents.includes(eventDef.eventType)}
              onChange={(e) =>
                handleEventToggle(eventDef.eventType, e.target.checked)
              }
              disabled={disabled || useAllEvents}
            />
          </Flex.Item>
        ))}
        {eventDefinitions.length === 0 && (
          <Text isMuted>No events available for this producer</Text>
        )}
      </Flex>
      {useAllEvents && eventDefinitions.length > 0 && (
        <Text isMuted style={{ fontSize: "0.8em", marginLeft: "8px", marginTop: "4px" }}>
          Uncheck "All events" above to select specific events
        </Text>
      )}
    </div>
  );
};
